import { Injectable } from '@angular/core';

import { Observable, forkJoin, of, combineLatest, Subject } from 'rxjs';
import {
    map,
    switchMap,
    startWith,
    shareReplay,
    first,
    concatMap,
} from 'rxjs/operators';

import { EntityInfo } from './entity-info';
import {
    ValidationMetadata,
    ValidatorMetadata,
    PropertyMetadata,
} from './validation-metadata';
import {
    ValidationContext,
    ValidationContextBase,
    GlobalValidationContext,
} from './validation-context';
import { ValidationError } from './validation-error';

export type ValidationResult = ValidationError[] | null;

export type ValidatorPredicate = (
    validator: ValidatorMetadata,
    property?: PropertyMetadata
) => boolean;

export interface EntitiesValidationResult {
    entities: EntityInfo[];
    validationErrors: ValidationError[];
}

export interface EntityStore {
    entities$: Observable<EntityInfo[]>;
}

export class ValidationOptions {
    // Optional predicate to control execution of provided validator
    predicate?: ValidatorPredicate;

    // Global context values, Will be passed to each validator as part of the context
    context?: GlobalValidationContext;

    // An external observable if emitting a value should trigger revalidation
    event$?: Observable<any>;

    // An optional function that allows context substitutions for the purpose of evaluating conditions
    conditionContextResolver?: (
        context: ValidationContext
    ) => ValidationContext;
}

@Injectable({ providedIn: 'root' })
export class ValidationService {
    private modelValidated = new Subject<{
        modelInfo: EntityInfo;
        validationErrors: ValidationError[];
    }>();

    constructor(readonly validationMetadata: ValidationMetadata) {}

    get modelValidated$(): Observable<{
        modelInfo: EntityInfo;
        validationErrors: ValidationError[];
    }> {
        return this.modelValidated.asObservable();
    }

    /**
     * Executes all asynchronous validation rules defined for the provided entityType.
     *
     * @param model The model to be validated
     * @param modelType The type of the model to be validated
     * @param async true to execute asynchronous validators, false to execute synchronous validators
     * @param options Optional options objects
     */
    validateModel(
        model: any,
        modelType: string,
        async: true,
        options?: ValidationOptions
    ): Observable<ValidationResult>;

    /**
     * Executes all synchronous validation rules defined for the provided entityType.
     *
     * @param model The model to be validated
     * @param modeltype The type of the model to be validated
     * @param async true to execute asynchronous validators, false to execute synchronous validators
     * @param options Optional options objects
     */
    validateModel(
        model: any,
        modeltype: string,
        async?: boolean,
        options?: ValidationOptions
    ): ValidationResult;

    validateModel(
        model: any,
        modelType: string,
        async?: boolean,
        options?: ValidationOptions
    ): Observable<ValidationResult> | ValidationResult {
        let validationErrors: (
            | Observable<ValidationError | ValidationError[]>
            | ValidationError
        )[] = [];
        const predicate =
            (options && options.predicate) || ((): boolean => true);
        const contextResolver =
            (options && options.conditionContextResolver) ||
            ((context): ValidationContext => context);

        const modelValidationMetadata =
            this.validationMetadata.getModelConfig(modelType);
        if (modelValidationMetadata) {
            // Execute model level validators
            const modelValidators = this.filterValidators(
                modelValidationMetadata.validators,
                async,
                predicate
            );
            validationErrors = [
                ...validationErrors,
                ...modelValidators
                    .map((modelValidator) => {
                        const context: ValidationContext = {
                            ...((options && options.context) || {}),
                            model: model,
                            modelType: modelType,
                            metadata: modelValidationMetadata,
                            isClient: true,
                            isServer: false,
                            ...(modelValidator.context as ValidationContextBase),
                        };
                        if (
                            !modelValidator.evalCondition(
                                context,
                                contextResolver
                            )
                        ) {
                            return async
                                ? of(null as ValidationError)
                                : (null as ValidationError);
                        }
                        const validator = modelValidator.getValidator();
                        return validator.validate(model, context);
                    })
                    .reduce((prev, curr) => {
                        if (Array.isArray(curr)) {
                            return [...prev, ...curr];
                        }

                        return [...prev, curr];
                    }, [] as (Observable<ValidationError | ValidationError[]> | ValidationError)[]),
            ];

            // Execute property validators
            const properties = modelValidationMetadata.properties;
            properties.forEach((propertyMetadata) => {
                const propertyValidators = this.filterValidators(
                    propertyMetadata.validators,
                    async,
                    (validator) => predicate(validator, propertyMetadata)
                );
                validationErrors = [
                    ...validationErrors,
                    ...propertyValidators
                        .map((propertyValidator) => {
                            const context: ValidationContext = {
                                ...((options && options.context) || {}),
                                model: model,
                                modelType: modelType,
                                metadata: modelValidationMetadata,
                                propertyName: propertyMetadata.propertyName,
                                displayName: propertyMetadata.displayName,
                                isClient: true,
                                isServer: false,
                                ...(propertyValidator.context as ValidationContextBase),
                            };
                            if (
                                !propertyValidator.evalCondition(
                                    context,
                                    contextResolver
                                )
                            ) {
                                return async ? of(null) : null;
                            }
                            const validator = propertyValidator.getValidator();
                            return validator.validate(
                                model[propertyMetadata.propertyName],
                                context
                            );
                        })
                        .reduce((prev, curr) => {
                            if (Array.isArray(curr)) {
                                return [...prev, ...curr];
                            }

                            return [...prev, curr];
                        }, [] as (Observable<ValidationError | ValidationError[]> | ValidationError)[]),
                ];
            });
        }

        if (async) {
            const asyncErrors = validationErrors as Observable<
                ValidationError | ValidationError[]
            >[];
            const asyncResult = forkJoin(asyncErrors).pipe(
                map((errors) => {
                    let flatErrors = errors
                        .reduce<ValidationError[]>((prev, curr) => {
                            if (Array.isArray(curr)) {
                                return [...prev, ...curr];
                            }

                            return [...prev, curr];
                        }, [])
                        .filter((error) => !!error);
                    flatErrors = flatErrors.length ? flatErrors : null;
                    this.modelValidated.next({
                        modelInfo: { entity: model, entityType: modelType },
                        validationErrors: flatErrors,
                    });
                    return flatErrors;
                })
            );

            return asyncResult;
        }

        validationErrors = validationErrors.filter((error) => !!error);
        const result = validationErrors.length
            ? (validationErrors as any)
            : null;
        this.modelValidated.next({
            modelInfo: { entity: model, entityType: modelType },
            validationErrors: result,
        });
        return result;
    }

    /**
     * Executes all asynchronous and synchronous validation rules for all entities in the cache
     *
     * @param entityStore A store providing the entities to be validated
     * @param options Optional options
     * @returns Observable emitting validation errors and entity info
     */
    validateEntityStore(
        entityStore: EntityStore,
        options?: ValidationOptions
    ): Observable<EntitiesValidationResult> {
        return this.getTrigger(entityStore, options).pipe(
            switchMap((entities) => {
                if (entities.length === 0) {
                    return this.emptyValidationObservableResponse(entities);
                }
                const syncValidations = this.getSyncValidators(
                    entities,
                    options
                );
                const asyncValidations = this.getAsyncValidators(
                    entities,
                    options
                );
                return this.reduceValidation(
                    entities,
                    combineLatest([syncValidations, ...asyncValidations])
                );
            }),
            shareReplay(1)
        );
    }

    /**
     * Executes all asynchronous and synchronous validation rules for all entities in the cache as a one-time emit
     *
     * @param entityStore A store providing the entities to be validated
     * @param options Optional options
     * @returns One Time-Value Observable emitting validation errors and entity info
     */
    validateEntityStoreOneTime(
        entityStore: EntityStore,
        options?: ValidationOptions
    ): Observable<EntitiesValidationResult> {
        return this.getTrigger(entityStore, options).pipe(
            first(),
            concatMap((entities) => {
                if (entities.length === 0) {
                    return this.emptyValidationObservableResponse(entities);
                }
                const syncValidations = this.getSyncValidators(
                    entities,
                    options
                );
                const asyncValidations = this.getAsyncValidators(
                    entities,
                    options
                );
                return this.reduceValidation(
                    entities,
                    forkJoin([syncValidations, ...asyncValidations])
                );
            })
        );
    }

    private getTrigger(
        entityStore: EntityStore,
        options?: ValidationOptions
    ): Observable<EntityInfo[]> {
        options = options || {};

        return combineLatest([
            entityStore.entities$,
            this.validationMetadata.metadataChanged$.pipe(
                startWith(this.validationMetadata)
            ),
            options.event$
                ? options.event$.pipe(startWith(null as any))
                : of(null),
        ]).pipe(map(([entities]) => entities));
    }

    private emptyValidationObservableResponse(
        entities: EntityInfo[]
    ): Observable<EntitiesValidationResult> {
        return of({ entities, validationErrors: [] as ValidationError[] });
    }

    private getSyncValidators(
        entities: EntityInfo[],
        options?: ValidationOptions
    ): Observable<ValidationError[]> {
        return of(this.validateEntities(entities, false, options));
    }

    private getAsyncValidators(
        entities: EntityInfo[],
        options?: ValidationOptions
    ): Observable<ValidationError[]>[] {
        return this.validateEntities(entities, true, options).map((o) =>
            o.pipe(startWith<ValidationError[]>([]))
        );
    }

    private reduceValidation(
        entities: EntityInfo[],
        obs: Observable<ValidationError[][]>
    ): Observable<EntitiesValidationResult> {
        return obs.pipe(
            map((results) =>
                results.reduce((prev, curr) => [...prev, ...(curr || [])], [])
            ),
            map((validationErrors) => ({ entities, validationErrors }))
        );
    }

    private filterValidators(
        validators: ValidatorMetadata[],
        async: boolean,
        predicate: (validator: ValidatorMetadata) => boolean
    ): ValidatorMetadata[] {
        if (!async) {
            return validators.filter(
                (validator) => !validator.async && predicate(validator)
            );
        }

        return validators.filter(
            (validator) => validator.async && predicate(validator)
        );
    }

    private validateEntities(
        entities: EntityInfo[],
        async?: boolean,
        options?: ValidationOptions
    ): ValidationError[];
    private validateEntities(
        entities: EntityInfo[],
        async?: true,
        options?: ValidationOptions
    ): Observable<ValidationError[]>[];
    private validateEntities(
        entities: EntityInfo[],
        async?: boolean,
        options?: ValidationOptions
    ): ValidationError[] | Observable<ValidationError[]>[] {
        const contextResolver =
            (options && options.conditionContextResolver) ||
            ((context): ValidationContext => context);
        const conditionalEntities = entities.filter((entityInfo) => {
            const modelMetadata = this.validationMetadata.getModelConfig(
                entityInfo.entityType
            );
            return (
                modelMetadata &&
                modelMetadata.evalCondition(
                    entities,
                    entityInfo.entity,
                    contextResolver
                )
            );
        });

        if (!async) {
            return conditionalEntities
                .map((entityInfo) =>
                    this.validateModel(
                        entityInfo.entity,
                        entityInfo.entityType,
                        false,
                        options
                    )
                )
                .reduce((prev, curr) => [...prev, ...(curr || [])], []);
        }

        return conditionalEntities.map((entityInfo) =>
            this.validateModel(
                entityInfo.entity,
                entityInfo.entityType,
                true,
                options
            )
        );
    }
}
